import { get } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FormAddressSearch from '~/components/Form/next/FormAddressSearch';
import { Blockquote, Button } from '~/components/ui';
import { WYSIWYGText } from '~/components/WYSIWYGText';
import { useIsFormularEditMode } from '../FormularModeContext';
import { getVisibleSectionChildren } from '../helpers/sectionVisibility';
import CheckboxField from './CheckboxField';
import FormularDateInput from './FormularDateInput';
import FormularFileInput from './FormularFileInput';
import FormularIBANInput from './FormularIBANInput';
import FormularPricingTableInput from './FormularPricingTableInput';
import FormularSelectInput from './FormularSelectInput';
import FormularSignatureInput from './FormularSignatureInput';
import FormularTextInput from './FormularTextInput';
import FormularTextareaInput from './FormularTextareaInput';
import TableField from './TableField';

// Renders a single form field definition as the matching input component.
const Field = ({ field, namePrefix = 'formData' }) => {
    const name = namePrefix ? namePrefix + '.' + field.name : field.name;
    const label = field.label + (field.rules?.includes('required') ? ' *' : '');
    const helperText = field.helperText?.trim() || undefined;

    switch (field.type) {
        case 'select':
            return <FormularSelectInput name={name} label={label} helperText={helperText} options={field?.options} />;

        case 'payment_options':
            return (
                <FormularSelectInput
                    name={name}
                    label={label}
                    helperText={helperText}
                    optionsWithKey={field?.options?.map((opt) => ({ value: opt.price, label: opt.option }))}
                />
            );

        case 'date':
            return (
                <FormularDateInput
                    name={name}
                    label={label}
                    helperText={helperText}
                    minDaysRelative={field.dateMinDaysRelative}
                    maxDaysRelative={field.dateMaxDaysRelative}
                    snapTo={field.dateSnapTo}
                    snapDay={field.dateSnapDay}
                />
            );

        case 'address':
            return (
                <FormAddressSearch
                    name={name}
                    label={label}
                    required={field.rules?.includes('required')}
                    inputProps={{ helperText }}
                />
            );

        case 'table':
            return <TableField name={name} label={label} field={field} />;

        case 'checkbox':
            return <CheckboxField name={name} label={label} helperText={helperText} field={field} />;

        case 'wysiwyg':
            return <WYSIWYGText>{field.text}</WYSIWYGText>;

        case 'section':
            return <SectionField field={field} namePrefix={namePrefix} />;

        case 'number':
            return (
                <FormularTextInput
                    name={name}
                    type="number"
                    label={label}
                    helperText={helperText}
                    placeholder={field.placeholder}
                />
            );

        case 'file':
            return (
                <FormularFileInput
                    name={name}
                    label={label}
                    helperText={helperText}
                    acceptedFileTypes={field.acceptedFileTypes}
                />
            );

        case 'textarea':
            return (
                <FormularTextareaInput
                    name={name}
                    label={label}
                    helperText={helperText}
                    placeholder={field.placeholder}
                    rows={field.rows}
                />
            );

        case 'iban':
            return <FormularIBANInput name={name} label={label} helperText={helperText} />;

        case 'pricing_table':
            return (
                <FormularPricingTableInput
                    name={name}
                    label={label}
                    helperText={helperText}
                    formItemGroupId={field.formItemGroupId}
                    selectionMode={field.selectionMode}
                    showPriceOverview={field.showPriceOverview}
                    displayLayout={field.displayLayout}
                    columnLabelLeft={field.columnLabelLeft}
                    columnLabelRight={field.columnLabelRight}
                />
            );

        case 'signature':
            return <FormularSignatureInput name={name} label={label} helperText={helperText} />;

        default:
            return (
                <FormularTextInput
                    name={name}
                    type={field.inputType}
                    label={label}
                    helperText={helperText}
                    placeholder={field.placeholder}
                />
            );
    }
};

// Collects the fully prefixed RHF field names of a field list, descending into sections.
const getFieldNames = (fields = [], currentPrefix) =>
    fields.flatMap((item) => {
        if (!item) return [];

        if (item.type === 'section') {
            const sectionChildren = Array.isArray(item.children) ? item.children : [];
            return sectionChildren.flatMap((child) => getFieldNames(child.fields ?? [], currentPrefix));
        }

        if (!item.name) return [];

        return [currentPrefix ? `${currentPrefix}.${item.name}` : item.name];
    });

// Renders a section: shows the branches matching the controlling field and steps through them.
const SectionField = ({ field, namePrefix }) => {
    const {
        trigger,
        setValue,
        getValues,
        formState: { errors, submitCount }
    } = useFormContext();
    // While editing a stored submission every step has to be reachable right away.
    const stepsUnlocked = useIsFormularEditMode();
    const [activeChildIndex, setActiveChildIndex] = useState(0);
    const [maxReachedChildIndex, setMaxReachedChildIndex] = useState(0);
    const isFirstRenderRef = useRef(true);
    const watchName = field.dependsOnField
        ? namePrefix
            ? `${namePrefix}.${field.dependsOnField}`
            : field.dependsOnField
        : null;
    const controllingValue = useWatch({ name: watchName, disabled: !watchName });

    const visibleChildren = useMemo(
        () => getVisibleSectionChildren(field, controllingValue),
        [controllingValue, field, namePrefix]
    );

    const allChildren = Array.isArray(field.children) ? field.children : [];
    // A stored value that matches no branch would leave the whole section blank and unfixable,
    // so while editing we fall back to the branches that actually carry answers.
    const isUnmatchedFallback =
        stepsUnlocked &&
        field.sectionMode === 'conditional' &&
        Boolean(field.dependsOnField) &&
        visibleChildren.length === 0 &&
        allChildren.length > 0;

    const renderedChildren = !isUnmatchedFallback
        ? visibleChildren
        : (() => {
              const hasValue = (child) =>
                  getFieldNames(child.fields ?? [], namePrefix).some((name) => {
                      const value = get(getValues(), name);
                      if (Array.isArray(value)) return value.length > 0;
                      return value !== undefined && value !== null && value !== '';
                  });

              const withValues = allChildren.filter(hasValue);
              return withValues.length > 0 ? withValues : allChildren;
          })();

    const visibleChildrenKey = visibleChildren.map((c) => c.id).join(',');

    // On failed submit, jump to the first step that has errors
    useEffect(() => {
        if (submitCount === 0) return;
        for (let i = 0; i < renderedChildren.length; i++) {
            const names = getFieldNames(renderedChildren[i].fields ?? [], namePrefix);
            if (names.some((n) => get(errors, n))) {
                setActiveChildIndex(i);
                setMaxReachedChildIndex((prev) => Math.max(prev, i));
                break;
            }
        }
    }, [submitCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        setActiveChildIndex(0);
        setMaxReachedChildIndex(0);
        if (field.sectionMode === 'conditional') {
            const visibleIds = new Set(renderedChildren.map((c) => c.id));
            (field.children ?? [])
                .filter((c) => !visibleIds.has(c.id))
                .forEach((child) => {
                    getFieldNames(child.fields ?? [], namePrefix).forEach((name) => {
                        setValue(name, undefined, { shouldDirty: false, shouldValidate: false });
                    });
                });
        }
    }, [visibleChildrenKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (renderedChildren.length === 0) return null;

    const activeChild = renderedChildren[activeChildIndex] ?? null;
    const showChildHeader = activeChild && (activeChild.label || activeChild.helperText);
    const isFirstStep = activeChildIndex === 0;
    const isLastStep = activeChildIndex === renderedChildren.length - 1;

    // Steps one branch back.
    const handlePrevious = () => {
        if (isFirstStep) return;
        setActiveChildIndex((current) => current - 1);
    };

    // Validates the current branch before stepping forward, unless the steps are free to jump.
    const handleNext = async () => {
        if (isLastStep || !activeChild) return;

        if (!stepsUnlocked) {
            const fieldNames = getFieldNames(activeChild.fields ?? [], namePrefix);
            const isValid = fieldNames.length > 0 ? await trigger(fieldNames) : true;
            if (!isValid) return;
        }

        const nextIndex = activeChildIndex + 1;
        setMaxReachedChildIndex((current) => Math.max(current, nextIndex));
        setActiveChildIndex(nextIndex);
    };

    return (
        <section
            className={`${field.sectionMode !== 'conditional' && 'tw-card-bordered tw-border-accents-5'} tw-my-5 tw-space-y-4`}
        >
            {(field.label || field.helperText) && (
                <div className="tw-pl-2">
                    <h3 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-accents-9">{field.label}</h3>
                    {field.helperText ? (
                        <p className="tw-mb-0 tw-max-w-2xl tw-whitespace-pre-line tw-text-sm tw-leading-6 tw-text-accents-6">
                            {field.helperText}
                        </p>
                    ) : null}
                </div>
            )}

            {isUnmatchedFallback && (
                <Blockquote color="warning" className="tw-mb-2">
                    Die gespeicherten Angaben passen zu keiner der hinterlegten Varianten dieses Abschnitts. Alle
                    Varianten werden angezeigt, damit du deine Angaben bearbeiten kannst.
                </Blockquote>
            )}

            {renderedChildren.length > 1 ? (
                <div className="tw-space-y-3 tw-border-0 tw-border-b tw-border-solid tw-border-accents-5 tw-pb-4">
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {renderedChildren.map((child, index) => {
                            const stepNames = getFieldNames(child.fields ?? [], namePrefix);
                            const hasErrors = submitCount > 0 && stepNames.some((n) => get(errors, n));
                            const isCompleted =
                                !stepsUnlocked && index < activeChildIndex && index <= maxReachedChildIndex;
                            const isCurrent = index === activeChildIndex;
                            const isLocked = !stepsUnlocked && index > maxReachedChildIndex;

                            return (
                                <button
                                    key={child.id}
                                    type="button"
                                    className={`tw-flex tw-min-w-[160px] tw-flex-1 tw-transform-none tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors active:tw-translate-y-0 active:tw-scale-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
                                        hasErrors
                                            ? 'tw-border-error/40 tw-bg-error/5 tw-text-error'
                                            : isCurrent
                                              ? 'tw-border-success tw-bg-success/10 tw-text-success'
                                              : isCompleted
                                                ? 'tw-border-success/30 tw-bg-success/[0.04] tw-text-accents-8 hover:tw-border-success/50 hover:tw-bg-success/[0.07]'
                                                : isLocked
                                                  ? 'tw-border-accents-2 tw-bg-accents-0 tw-text-accents-5'
                                                  : 'tw-border-accents-3 tw-bg-transparent tw-text-accents-8 hover:tw-border-accents-4 hover:tw-bg-accents-0'
                                    }`}
                                    onClick={() => setActiveChildIndex(index)}
                                    disabled={isLocked}
                                >
                                    <span
                                        className={`tw-inline-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold ${
                                            hasErrors
                                                ? 'tw-bg-error/10 tw-text-error'
                                                : isCurrent
                                                  ? 'tw-bg-success tw-text-white'
                                                  : isCompleted
                                                    ? 'tw-bg-success/15 tw-text-success'
                                                    : 'tw-bg-accents-1 tw-text-accents-7'
                                        }`}
                                    >
                                        {hasErrors ? '!' : isCompleted ? '✓' : index + 1}
                                    </span>
                                    <span className="tw-flex tw-min-w-0 tw-flex-col">
                                        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-accents-5">
                                            Schritt {index + 1}
                                        </span>
                                        <span className="tw-truncate tw-text-sm tw-font-medium">
                                            {child.label || `Schritt ${index + 1}`}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {!stepsUnlocked && maxReachedChildIndex < renderedChildren.length - 1 && (
                        <p className="tw-mb-0 tw-text-xs tw-text-accents-6">
                            Bitte alle Schritte durchgehen, bevor du das Formular absendest.
                        </p>
                    )}
                </div>
            ) : null}

            {activeChild && (
                <>
                    {showChildHeader && (
                        <div className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-5 tw-pb-1">
                            {activeChild.label && (
                                <h4 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-accents-8">
                                    {activeChild.label}
                                </h4>
                            )}
                            {activeChild.helperText && (
                                <p className="tw-mb-0 tw-whitespace-pre-line tw-text-sm tw-leading-6 tw-text-accents-6">
                                    {activeChild.helperText}
                                </p>
                            )}
                        </div>
                    )}

                    {(activeChild.fields ?? []).map((childField) => (
                        <div key={childField.name} className="tw-mb-3">
                            <Field field={childField} namePrefix={namePrefix} />
                        </div>
                    ))}

                    {renderedChildren.length > 1 ? (
                        <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-3 tw-pt-2">
                            <button
                                type="button"
                                className="tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-accents-7 tw-transition-colors hover:tw-border-accents-4 hover:tw-bg-accents-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                                onClick={handlePrevious}
                                disabled={isFirstStep}
                            >
                                Zurück
                            </button>

                            {!isLastStep && (
                                <Button type="button" flat onClick={handleNext}>
                                    Weiter
                                </Button>
                            )}
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
};

export default Field;
